import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	selectEventGroupSummaries,
	selectSelectedUpsellEvents,
	selectSelectedUpsellProducts,
	setSelectedUpsellEvent,
	setSelectedUpsellProduct
} from '@ameroservices-platform/attraction-frontend/app/store/shared/cartSlice';
import moment from 'moment-timezone';
import { Button, TextField, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import getColor from '@ameroservices-platform/attraction-frontend/app/main/apps/content/cmsContent/cmsComponents/shared/getColor';
import { selectMainTheme } from '@ameroservices-platform/attraction-frontend/app/store/fuse/settingsSlice';
import { mergeThemes, selectTheme } from '@ameroservices-platform/attraction-frontend/app/store/shared/frontendSlice';
import { selectCartOrderLines } from '@ameroservices-platform/attraction-frontend/app/store/shared/userSlice';
import Autocomplete from '@mui/material/Autocomplete';
import Icon from '@mui/material/Icon';
import Popover from '@mui/material/Popover';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import clsx from 'clsx';
import Calendar from 'react-calendar';

let locale = 'da-DK';
const language = localStorage.getItem('language') ? JSON.parse(localStorage.getItem('language')) : { isoCode: 'da' };
if (language && language.isoCode && language.isoCode !== 'da') {
	locale = language.isoCode;
}

// Can only be used on upsell module in cart
function UpsellModuleTimeslotSelector({ dataFromExecutor, contentElementProps }) {
	const { eventGroup, selectedDate: forcedSelectedDate, upsellProducts } = dataFromExecutor;
	const orderLines = useSelector(selectCartOrderLines);
	const eventGroupSummaries = useSelector(selectEventGroupSummaries);
	const selectedUpsellProducts = useSelector(selectSelectedUpsellProducts);
	const selectedUpsellEvents = useSelector(selectSelectedUpsellEvents);
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const mainTheme = useSelector(selectMainTheme);
	const theme = useSelector(selectTheme);
	const mergedTheme = mergeThemes(mainTheme, theme);
	const [selectedDate, setSelectedDate] = useState(
		contentElementProps.enableDateSelector ? forcedSelectedDate ?? moment() : moment()
	);
	const [anchorEl, setAnchorEl] = useState(null);

	useEffect(() => {
		// If the eventGroup has a selected upsell event, set the selected date to that date
		if (selectedUpsellEvents?.[eventGroup.id]) {
			for (const summary of eventGroupSummaries?.[eventGroup.id] || []) {
				for (const date of Object.values(summary.dates || {})) {
					for (const time of Object.values(date.times || {})) {
						if (time.eventUid === selectedUpsellEvents?.[eventGroup.id]) {
							setSelectedDate(moment(date.date.toDate()));
							return;
						}
					}
				}
			}
		} else if (contentElementProps.enableDateSelector) {
			setSelectedDate(moment());
		} else if (forcedSelectedDate) {
			setSelectedDate(forcedSelectedDate);
		} else {
			setSelectedDate(moment());
		}
	}, [contentElementProps, forcedSelectedDate]);

	const dateOptions = useMemo(() => {
		if (!contentElementProps.useDropdownSelector || !contentElementProps.enableDateSelector) {
			return [];
		}
		if (!eventGroupSummaries) {
			return [];
		}
		const groupSummaries = eventGroupSummaries[eventGroup?.id];
		if (!groupSummaries) {
			return [];
		}
		return groupSummaries.reduce((acc, cur) => {
			Object.values(cur.dates).forEach(date => {
				const dateMoment = moment(date.date.toDate());
				if (dateMoment.isSameOrAfter(moment(), 'day')) {
					acc.push(dateMoment.startOf('day').valueOf());
				}
			});
			return acc;
		}, []);
	}, [contentElementProps, eventGroup, eventGroupSummaries]);

	const useStyles = makeStyles(_theme => ({
		root: {
			color: `${getColor(contentElementProps.textColor, contentElementProps.customTextColor, mergedTheme)}`,
			borderColor: `${getColor(contentElementProps.textColor, contentElementProps.customTextColor, mergedTheme)}`,
			backgroundColor: `${getColor(
				contentElementProps.backgroundColor,
				contentElementProps.customBackgroundColor,
				mergedTheme
			)}`,
			'&:hover': {
				color: `${getColor(
					contentElementProps.textColorHover,
					contentElementProps.customTextColorHover,
					mergedTheme
				)}`,
				borderColor: `${getColor(
					contentElementProps.textColorHover,
					contentElementProps.customTextColorHover,
					mergedTheme
				)}`,
				backgroundColor: `${getColor(
					contentElementProps.backgroundColorHover,
					contentElementProps.customBackgroundColorHover,
					mergedTheme
				)}`
			}
		},
		rootActive: {
			color: `${getColor(
				contentElementProps.activeTextColor,
				contentElementProps.activeCustomTextColor,
				mergedTheme
			)}`,
			borderColor: `${getColor(
				contentElementProps.activeTextColor,
				contentElementProps.activeCustomTextColor,
				mergedTheme
			)}`,
			backgroundColor: `${getColor(
				contentElementProps.activeBackgroundColor,
				contentElementProps.activeCustomBackgroundColor,
				mergedTheme
			)}`,
			'&:hover': {
				color: `${getColor(
					contentElementProps.activeTextColorHover,
					contentElementProps.activeCustomTextColorHover,
					mergedTheme
				)}`,
				borderColor: `${getColor(
					contentElementProps.activeTextColorHover,
					contentElementProps.activeCustomTextColorHover,
					mergedTheme
				)}`,
				backgroundColor: `${getColor(
					contentElementProps.activeBackgroundColorHover,
					contentElementProps.activeCustomBackgroundColorHover,
					mergedTheme
				)}`
			}
		},
		calendarPicker: {
			// width: '100%',
			// overflow: 'hidden',
			'& .react-calendar__navigation': {
				display: 'flex'
			},

			// All days as a group
			'& .react-calendar__navigation__arrow': {
				width: '70px',
				height: '40px',
				color: `${getColor(contentElementProps.textColor, contentElementProps.customTextColor, mergedTheme)}`,
				background: 'transparent'
			},
			'& .react-calendar__navigation__label': {
				color: `${getColor(contentElementProps.textColor, contentElementProps.customTextColor, mergedTheme)}`,
				background: 'transparent'
			},

			// Weekdays - man, tir, ons...
			'& .react-calendar__month-view__weekdays__weekday abbr': {
				textDecoration: 'none',
				color: `${getColor(contentElementProps.textColor, contentElementProps.customTextColor, mergedTheme)}`
			},

			// All days individual
			'& .react-calendar__month-view__days__day': {
				backgroundColor: `${getColor(
					contentElementProps.backgroundColor,
					contentElementProps.customBackgroundColor,
					mergedTheme
				)}`,
				color: `${getColor(contentElementProps.textColor, contentElementProps.customTextColor, mergedTheme)}`,
				outline: '2px solid #FDFBF7',
				'& abbr': {
					display: 'none'
				},
				'& div': {
					padding: '10px 1px'
				}
			},

			// Active button (selected date)
			'& .react-calendar__tile.react-calendar__tile--active': {
				backgroundColor: `${getColor(
					contentElementProps.activeBackgroundColor,
					contentElementProps.activeCustomBackgroundColor,
					mergedTheme
				)}`,
				color: `${getColor(
					contentElementProps.activeTextColor,
					contentElementProps.activeCustomTextColor,
					mergedTheme
				)}`
			},

			// Disabled buttons (previous dates)
			'& button:disabled.react-calendar__month-view__days__day': {
				opacity: '0.3'
			},

			// Todays date
			'& .react-calendar__tile--now': {}
		}
	}));

	const classes = useStyles();

	const summaryForSelectedDate = useMemo(() => {
		if (!selectedDate) {
			return null;
		}
		if (!eventGroupSummaries) {
			return null;
		}
		const groupSummaries = eventGroupSummaries[eventGroup?.id];
		if (!groupSummaries) {
			return null;
		}
		const groupSummary = groupSummaries.find(summary => summary.month === selectedDate.format('M-YYYY'));
		if (!groupSummary) {
			return null;
		}
		const times = groupSummary.dates?.[selectedDate.date()]?.times;
		if (!times) {
			return null;
		}
		const mappedTimes = Object.entries(times).map(([time, timeslot]) => {
			return {
				id: time,
				...timeslot,
				start: moment(timeslot.start.toDate()),
				end: moment(timeslot.end.toDate())
			};
		});

		mappedTimes.sort((a, b) => a.start - b.start);

		return mappedTimes;
	}, [eventGroupSummaries, selectedDate, eventGroup?.id]);

	const maximumAmount = useMemo(() => {
		return orderLines.filter(orderLine => orderLine.upsellProducts).reduce((acc, cur) => acc + cur.qty, 0);
	}, [orderLines]);

	function handleSelectEvent(event) {
		const upsellProduct = upsellProducts.length === 1 ? upsellProducts[0] : null;
		if (selectedUpsellEvents?.[eventGroup.id] === event.eventUid) {
			if (upsellProduct) {
				const amount = selectedUpsellProducts?.[eventGroup.id]?.[upsellProduct.id] || 0;
				if (event.available <= amount || amount >= maximumAmount) {
					return;
				}
				dispatch(
					setSelectedUpsellProduct({
						eventGroupUid: eventGroup.id,
						productUid: upsellProduct.id,
						amount: amount + 1
					})
				);
			} else {
				dispatch(setSelectedUpsellEvent({ eventGroupUid: eventGroup.id, eventUid: null }));
			}
		} else {
			dispatch(setSelectedUpsellEvent({ eventGroupUid: eventGroup.id, eventUid: event.eventUid }));
			if (upsellProduct) {
				const amount = selectedUpsellProducts?.[eventGroup.id]?.[upsellProduct.id] || 0;
				if (amount === 0) {
					dispatch(
						setSelectedUpsellProduct({
							eventGroupUid: eventGroup.id,
							productUid: upsellProduct.id,
							amount: 1
						})
					);
				}
			}
		}
	}

	const productAmountSum = useMemo(() => {
		return Object.values(selectedUpsellProducts?.[eventGroup.id] || {}).reduce((acc, cur) => acc + cur, 0);
	}, [selectedUpsellProducts, eventGroup]);

	return (
		<>
			<Grid container spacing={1}>
				{contentElementProps.enableDateSelector && (
					<Grid item xs={12}>
						{contentElementProps.useDropdownSelector && (
							<Autocomplete
								className="w-full"
								options={dateOptions}
								value={selectedDate.valueOf()}
								onChange={(event, newValue) => {
									setSelectedDate(moment(newValue));
								}}
								size="small"
								getOptionLabel={option => {
									return moment(option).format(t('LONG_DATE_FORMAT'));
								}}
								renderInput={params => <TextField {...params} label="Dato" variant="outlined" />}
								openOnFocus
								required
								fullWidth
								disableClearable
							/>
						)}
						{!contentElementProps.useDropdownSelector && (
							<>
								<Button
									classes={{
										root: classes.root
									}}
									className="w-full rounded-none normal-case"
									variant={'outlined'}
									startIcon={<Icon>event</Icon>}
									onClick={e => {
										setAnchorEl(e.currentTarget);
									}}
								>
									{selectedDate.format(t('LONG_DATE_FORMAT'))}
								</Button>
								<Popover
									id="popover-range"
									open={!!anchorEl}
									anchorEl={anchorEl}
									onClose={() => setAnchorEl(null)}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'center'
									}}
									transformOrigin={{
										vertical: 'top',
										horizontal: 'center'
									}}
								>
									<Calendar
										locale={locale || 'da-DK'}
										next2Label={null}
										prev2Label={null}
										prevLabel={<ArrowBackIcon />}
										nextLabel={<ArrowForwardIcon />}
										minDetail="month"
										onChange={value => {
											setSelectedDate(moment(value));
											setAnchorEl(null);
										}}
										value={selectedDate.toDate()}
										showNeighboringMonth={false}
										minDate={new Date()}
										className={clsx(classes.calendarPicker, 'text-center')}
										tileDisabled={tileData => {
											const date = tileData.date.getDate();
											const groupSummaries = eventGroupSummaries[eventGroup?.id];
											if (!groupSummaries) {
												return null;
											}
											const groupSummary = groupSummaries.find(
												summary => summary.month === moment(tileData.date).format('M-YYYY')
											);
											if (!groupSummary) {
												return null;
											}
											if (groupSummary && groupSummary.dates[date]) {
												if (groupSummary.dates[date].availableAtSameTime >= productAmountSum) {
													return false;
												}
											}
											return true;
										}}
										tileContent={d => {
											let tooltip = '';
											return (
												<Tooltip title={tooltip}>
													<div aria-label={moment(d.date).format('DD[.] MMMM YYYY')}>
														{d.date.getDate()}
													</div>
												</Tooltip>
											);
										}}
									/>
								</Popover>
							</>
						)}
					</Grid>
				)}
				{!summaryForSelectedDate?.length && (
					<Grid item xs={12}>
						<Typography
							className={'text-center'}
							classes={{
								root: classes.root
							}}
						>
							{t('NO_TIMES_AVAILABLE')}
						</Typography>
					</Grid>
				)}
				{summaryForSelectedDate?.map(timeslot => (
					<Grid item xs={6} sm={4} key={`${eventGroup.id}-${timeslot.id}`}>
						<Button
							variant="outlined"
							className={`w-full rounded-none normal-case`}
							disabled={
								timeslot.available <= 0 ||
								moment().isAfter(moment(timeslot.end)) ||
								timeslot.available < productAmountSum
							}
							onClick={() => {
								handleSelectEvent(timeslot);
							}}
							id={`timeslot-${timeslot.eventUid}`}
							classes={{
								root:
									selectedUpsellEvents?.[eventGroup.id] === timeslot.eventUid
										? classes.rootActive
										: classes.root
							}}
						>
							<span className="text-13 font-bold whitespace-no-wrap">
								{timeslot.start.format('HH:mm')} - {timeslot.end.format('HH:mm')}
							</span>
						</Button>
					</Grid>
				))}
			</Grid>
		</>
	);
}

export default UpsellModuleTimeslotSelector;
